import type { InspectionWarrantyStatus } from './inspection.service';
import { apiClient } from '@/services/api-client';

// ── Types (mirror the backend response DTOs; presentation layer only) ─────────

export type InspectionReportStatus =
  'DRAFT' | 'GENERATED' | 'UNDER_REVIEW' | 'REJECTED' | 'APPROVED';

export type ReportNumberSeparator = 'SLASH' | 'HYPHEN' | 'UNDERSCORE' | 'NONE';
export type ReportNumberYearFormat = 'NONE' | 'YY' | 'YYYY';
export type ReportNumberResetPolicy = 'NEVER' | 'YEARLY' | 'MONTHLY';

export interface InspectionReport {
  id: string;
  inspectionId: string;
  reportNumber: string | null;
  reportTitleSnapshot: string | null;
  /** @deprecated B6.11 (G-1) — renderer build-time constants, not this report's
   * design. Use `layoutVersion`. */
  templateName: string | null;
  /** @deprecated B6.11 (G-1) — see `templateName`. Use `layoutVersion`. */
  templateVersion: string | null;
  /** Version of the layout frozen into this report — what the PDF footer prints. */
  layoutVersion: number | null;
  status: InspectionReportStatus;
  publishedToCustomer: boolean;
  asFoundCondition: string | null;
  finalActionActivities: string | null;
  causeOfFailure: string | null;
  finalActionNeeded: string | null;
  finalActivity: string | null;
  clsProductQuality: boolean;
  clsApplicationEngineering: boolean;
  clsProductAbuse: boolean;
  serviceStartDate: string | null;
  serviceEndDate: string | null;
  rejectionReason: string | null;
  issuedAt: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  publishedAt: string | null;
  reopenedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Why the procedure requirement resolved as it did. Derived server-side from
 * the FROZEN procedure snapshot, so it can never change for an inspection after
 * capture — republishing a procedure as mandatory does not reach backwards.
 */
export type ProcedureRequirementState =
  'NOT_REQUIRED' | 'OPTIONAL' | 'MANDATORY_INCOMPLETE' | 'MANDATORY_COMPLETE';

export interface ReportReadiness {
  customerPresent: boolean;
  equipmentPresent: boolean;
  complaintPresent: boolean;
  /**
   * DEPRECATED NAME, kept for API compatibility. Now means "the procedure
   * requirement is satisfied" — identical to procedureRequirementMet. It no
   * longer counts individual checklist steps.
   */
  hasCompletedChecklist: boolean;
  procedureRequirementMet: boolean;
  procedureState: ProcedureRequirementState;
  hasFinding: boolean;
  hasPhoto: boolean;
  canGenerate: boolean;
}

export type ReportEventType =
  | 'DRAFT_CREATED'
  | 'GENERATED'
  | 'SUBMITTED_FOR_REVIEW'
  | 'REJECTED'
  | 'REOPENED'
  | 'APPROVED'
  | 'PUBLISHED'
  | 'UNPUBLISHED'
  | 'CUSTOMER_DOWNLOADED'
  | 'DOWNLOADED';

export interface ReportHistoryEvent {
  id: string;
  eventType: ReportEventType;
  actorUserId: string | null;
  actorRoleName: string | null;
  reason: string | null;
  createdAt: string;
}

export interface ReportHistoryResponse {
  data: ReportHistoryEvent[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ReportPhoto {
  id: string;
  attachmentId: string;
  position: number;
  /** Which photo row the image sits in (B6.8b). */
  rowIndex: number;
  /** The caption for this image IN THIS REPORT — never the attachment's global one. */
  caption: string | null;
  originalFileName: string | null;
}

/** B6.8d — a photo the engineer may place into the report. Includes images
 * uploaded for the report itself, which are never inspection evidence. */
export interface AvailableReportPhoto {
  attachmentId: string;
  origin: 'INSPECTION_EVIDENCE' | 'REPORT_CUSTOM';
  originalFileName: string;
  mimeType: string;
  fileSizeBytes: string;
  url: string | null;
  thumbnailUrl: string | null;
  caption: string | null;
  isSelected: boolean;
  createdAt: string;
}

/** B6.8b — the photo endpoints return the composition plus its row shape.
 * `rows` is images-per-row, e.g. [3, 2]. */
export interface ReportPhotoComposition {
  photos: ReportPhoto[];
  rows: number[];
  total: number;
  /** B6.8c — false means the report still uses the legacy two-per-row packing. */
  photosComposed: boolean;
}

/** One image placed in one row (B6.8b payload shape). */
export interface ReportPhotoPlacement {
  attachmentId: string;
  rowIndex: number;
  /**
   * Report-specific caption (B6.9). Sent with every placement, so the server
   * always knows the engineer's current intent:
   *   string → set it   ·   null → clear it   ·   omitted → keep what is stored
   * The composer always sends a value, so a cleared box genuinely clears.
   */
  caption?: string | null;
}

/** B6.8b changed the photo endpoints from a bare array to the composition
 * envelope. Only this layer knows: callers still receive the ordered photo
 * list, now carrying rowIndex. The row-aware composer arrives in B6.8e. */
function photosOf(payload: unknown): ReportPhoto[] {
  if (Array.isArray(payload)) return payload as ReportPhoto[];
  const photos = (payload as ReportPhotoComposition | null)?.photos;
  return Array.isArray(photos) ? photos : [];
}

/** A finding currently selected into the report (ordered). */
export interface ReportFinding {
  id: string;
  inspectionFindingId: string;
  position: number;
  severity: string;
  title: string;
}

/** A finding that may be selected — every non-deleted finding of the inspection. */
export interface AvailableReportFinding {
  id: string;
  findingNumber: number;
  severity: string;
  title: string;
  componentArea: string | null;
  sortOrder: number;
  selected: boolean;
}

/**
 * The report's finding composition.
 *
 * `findingsComposed` is what separates a report that has never been composed
 * (renders every non-deleted finding) from one deliberately composed with an
 * empty selection (renders none). The UI must preserve that distinction.
 */
export interface ReportFindingComposition {
  findingsComposed: boolean;
  selected: ReportFinding[];
  totalAvailable: number;
  /** Selected findings that have since been deleted; excluded from `selected`. */
  missingCount: number;
}

/**
 * ── Report section composition (B6.4) ───────────────────────────────────────
 *
 * Which sections the report contains and in what order. The backend owns the
 * merge, the normalisation and the sparse diff — the UI sends the complete
 * composition and renders whatever comes back.
 */
export type ReportSectionCompositionState = 'EDITABLE' | 'FROZEN' | 'LEGACY';

/**
 * One column of a section (B6.5a-3). Everything the width editor needs comes
 * from the API, so the UI never hardcodes column counts, widths or geometry.
 */
export interface ReportSectionColumn {
  position: number;
  /** Current effective width as a fraction of the section's content width. */
  widthFraction: number;
  contentKey: string | null;
  /**
   * EXCLUSIVE lower bound: a width must stay strictly above this. Derived
   * server-side from the padding the renderer subtracts before drawing into the
   * cell. 0 for non-resizable sections.
   */
  minWidthFraction: number;
}

export interface ReportSectionCompositionItem {
  sectionKey: string;
  /** The template's title, or null to use the renderer's built-in one. */
  title: string | null;
  /** The renderer's built-in title, so a null title can still be labelled. */
  defaultTitle: string | null;
  position: number;
  isVisible: boolean;
  /** True when this section deviates from the template. */
  hasOverride: boolean;
  /**
   * True when the template lets an engineer show or hide this section on ONE
   * report. False for the structural sections the template owns — the header,
   * meta grid, classification band and the mandatory signature block. Never
   * infer this from the section key; the API is the authority.
   */
  isEngineerToggleable: boolean;
  /**
   * True when the renderer derives this section's cell geometry from the
   * layout, so its column widths can be changed. Never infer this from the
   * section key — the API is the authority.
   */
  resizable: boolean;
  /** The section's columns in order. Column COUNT is fixed by the renderer. */
  columns: ReportSectionColumn[];
}

/**
 * `state` is what the editor keys off:
 *   EDITABLE — not yet generated; composition still shapes the report
 *   FROZEN   — generated; the layout snapshot is authoritative and immutable
 *   LEGACY   — generated before layout snapshots existed; renders from the
 *              live template and can never be composed
 */
export interface ReportSectionComposition {
  version: number;
  state: ReportSectionCompositionState;
  editable: boolean;
  composed: boolean;
  templateSlug: string;
  templateName: string;
  templateVersion: number;
  sections: ReportSectionCompositionItem[];
  overrides: Record<string, unknown> | null;
}

/** One row of the save payload. Positions come from the displayed order. */
export interface ReportSectionCompositionInput {
  sectionKey: string;
  isVisible: boolean;
  position: number;
  /** Sent only for resizable sections; omitted means "keep template widths". */
  columnWidths?: number[];
}

export interface UpdateReportMetadata {
  asFoundCondition?: string;
  finalActionActivities?: string;
  causeOfFailure?: string;
  finalActionNeeded?: string;
  finalActivity?: string;
  clsProductQuality?: boolean;
  clsApplicationEngineering?: boolean;
  clsProductAbuse?: boolean;
  serviceStartDate?: string;
  serviceEndDate?: string;
  /**
   * Warranty coverage. The only field here that is stored on the INSPECTION
   * rather than the report; the backend writes it in the same transaction.
   * Omit to leave the current status unchanged — never sent as a reset.
   */
  warrantyStatus?: InspectionWarrantyStatus;
}

/**
 * The display-only subset of the report configuration: enough to render the
 * shape of a report number, without the allocation policy.
 */
export interface ReportNumberPresentation {
  inspectionReportTitle: string;
  reportNumberPrefix: string;
  reportNumberSeparator: ReportNumberSeparator;
  reportNumberYearFormat: ReportNumberYearFormat;
  reportNumberSequenceLength: number;
}

export interface ReportSettings {
  inspectionReportTitle: string;
  reportNumberPrefix: string;
  reportNumberSeparator: ReportNumberSeparator;
  reportNumberYearFormat: ReportNumberYearFormat;
  reportNumberSequenceLength: number;
  /**
   * The LIVE MINIMUM for the next allocation — surfaced as "Next Report Number
   * (Minimum)". Every allocation takes `GREATEST(current + 1, this)`, so raising
   * it moves the next report forward and lowering it does nothing.
   */
  reportNumberStartingSequence: number;
  reportNumberResetPolicy: ReportNumberResetPolicy;
  /**
   * The formatted number the next report would receive, computed SERVER-SIDE by
   * the allocator's own preview.
   *
   * Read-only and never recomputed here: the browser has no access to the
   * sequence counter, and reproducing the rule in TypeScript would put it in two
   * places. Refetch the settings query to refresh it.
   */
  nextReportNumber: string;
}

const base = (inspectionId: string) => `/inspections/${inspectionId}/report`;

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Extracts a download filename from the Content-Disposition header. */
function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  return match?.[1] ?? fallback;
}

export const inspectionReportService = {
  // ── Report + metadata ──────────────────────────────────────────────────────
  async getReport(inspectionId: string): Promise<InspectionReport> {
    const r = await apiClient.get(base(inspectionId));
    return r.data as InspectionReport;
  },

  async createDraft(inspectionId: string): Promise<InspectionReport> {
    const r = await apiClient.post(base(inspectionId));
    return r.data as InspectionReport;
  },

  async updateMetadata(inspectionId: string, dto: UpdateReportMetadata): Promise<InspectionReport> {
    const r = await apiClient.patch(base(inspectionId), dto);
    return r.data as InspectionReport;
  },

  async getReadiness(inspectionId: string): Promise<ReportReadiness> {
    const r = await apiClient.get(`${base(inspectionId)}/readiness`);
    return r.data as ReportReadiness;
  },

  async getHistory(inspectionId: string, page = 1, pageSize = 50): Promise<ReportHistoryResponse> {
    const r = await apiClient.get(`${base(inspectionId)}/history`, { params: { page, pageSize } });
    return r.data as ReportHistoryResponse;
  },

  // ── Photos ───────────────────────────────────────────────────────────────
  async getPhotos(inspectionId: string): Promise<ReportPhoto[]> {
    const r = await apiClient.get(`${base(inspectionId)}/photos`);
    return photosOf(r.data);
  },

  // B6.8f: the legacy write helpers (setPhotos / reorderPhotos / removePhoto)
  // were retired here after this phase proved they had no remaining callers.
  // They sent the pre-B6.8b `attachmentIds` payload, which sets photosComposed
  // back to false — reachable only by mistake now that the composer owns photo
  // editing. The SERVER still accepts that payload for API compatibility; only
  // the dead client wrappers are gone.

  /** The composition plus its row shape and whether those rows are authoritative. */
  async getPhotoComposition(inspectionId: string): Promise<ReportPhotoComposition> {
    const r = await apiClient.get(`${base(inspectionId)}/photos`);
    const body = r.data as Partial<ReportPhotoComposition> | ReportPhoto[];
    if (Array.isArray(body)) {
      return { photos: body, rows: [], total: body.length, photosComposed: false };
    }
    return {
      photos: Array.isArray(body.photos) ? body.photos : [],
      rows: Array.isArray(body.rows) ? body.rows : [],
      total: body.total ?? 0,
      photosComposed: body.photosComposed === true,
    };
  },

  /**
   * B6.8e — saves the explicit row composition. Always the `{ photos }` shape:
   * that is what makes rowIndex authoritative (photosComposed = true). The
   * legacy `attachmentIds` payload must never come from the composer.
   */
  async setPhotoComposition(
    inspectionId: string,
    photos: ReportPhotoPlacement[],
  ): Promise<ReportPhotoComposition> {
    const r = await apiClient.put(`${base(inspectionId)}/photos`, { photos });
    const body = r.data as ReportPhotoComposition;
    return {
      photos: Array.isArray(body?.photos) ? body.photos : [],
      rows: Array.isArray(body?.rows) ? body.rows : [],
      total: body?.total ?? 0,
      photosComposed: body?.photosComposed === true,
    };
  },

  /** Every photo eligible for the report, evidence and report-uploaded alike. */
  async getAvailablePhotos(inspectionId: string): Promise<AvailableReportPhoto[]> {
    const r = await apiClient.get(`${base(inspectionId)}/photos/available`);
    return Array.isArray(r.data) ? (r.data as AvailableReportPhoto[]) : [];
  },

  /**
   * B6.8d — uploads an image for this report. The result is REPORT_CUSTOM: it
   * becomes available to the composer and never appears in inspection evidence.
   * Uploading does not place it in the report; call setPhotos to do that.
   */
  async uploadReportPhoto(
    inspectionId: string,
    file: File,
    caption?: string,
  ): Promise<AvailableReportPhoto> {
    const form = new FormData();
    form.append('file', file);
    if (caption !== undefined) form.append('caption', caption);
    const r = await apiClient.post(`${base(inspectionId)}/photos/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data as AvailableReportPhoto;
  },

  // ── Finding composition ────────────────────────────────────────────────────
  async getFindingComposition(inspectionId: string): Promise<ReportFindingComposition> {
    const r = await apiClient.get(`${base(inspectionId)}/findings`);
    return r.data as ReportFindingComposition;
  },

  async getAvailableFindings(inspectionId: string): Promise<AvailableReportFinding[]> {
    const r = await apiClient.get(`${base(inspectionId)}/findings/available`);
    return Array.isArray(r.data) ? (r.data as AvailableReportFinding[]) : [];
  },

  /** Replaces the whole ordered selection. The array order becomes the report order. */
  async setFindings(inspectionId: string, findingIds: string[]): Promise<ReportFindingComposition> {
    const r = await apiClient.put(`${base(inspectionId)}/findings`, { findingIds });
    return r.data as ReportFindingComposition;
  },

  // ── Workflow transitions ───────────────────────────────────────────────────
  async generate(inspectionId: string): Promise<InspectionReport> {
    const r = await apiClient.post(`${base(inspectionId)}/generate`);
    return r.data as InspectionReport;
  },
  async submit(inspectionId: string): Promise<InspectionReport> {
    const r = await apiClient.post(`${base(inspectionId)}/submit`);
    return r.data as InspectionReport;
  },
  async approve(inspectionId: string): Promise<InspectionReport> {
    const r = await apiClient.post(`${base(inspectionId)}/approve`);
    return r.data as InspectionReport;
  },
  async reject(inspectionId: string, reason: string): Promise<InspectionReport> {
    const r = await apiClient.post(`${base(inspectionId)}/reject`, { reason });
    return r.data as InspectionReport;
  },
  async reopen(inspectionId: string): Promise<InspectionReport> {
    const r = await apiClient.post(`${base(inspectionId)}/reopen`);
    return r.data as InspectionReport;
  },
  async publish(inspectionId: string): Promise<InspectionReport> {
    const r = await apiClient.post(`${base(inspectionId)}/publish`);
    return r.data as InspectionReport;
  },
  async unpublish(inspectionId: string): Promise<InspectionReport> {
    const r = await apiClient.post(`${base(inspectionId)}/unpublish`);
    return r.data as InspectionReport;
  },

  // ── PDF (streamed; never generated or cached in the browser) ────────────────
  /** Opens the server-rendered preview PDF in a new tab. */
  async openPreview(inspectionId: string): Promise<void> {
    const r = await apiClient.get(`${base(inspectionId)}/preview`, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data as Blob);
    window.open(url, '_blank', 'noopener');
    // Revoke shortly after so the new tab has time to load it.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  /** Downloads the server-rendered PDF using the server's filename. */
  async download(inspectionId: string): Promise<void> {
    const r = await apiClient.get(`${base(inspectionId)}/pdf`, { responseType: 'blob' });
    const filename = filenameFromDisposition(
      r.headers?.['content-disposition'] as string | undefined,
      'inspection-report.pdf',
    );
    triggerBlobDownload(r.data as Blob, filename);
  },

  // ── Section composition (B6.4) ─────────────────────────────────────────────
  async getSectionComposition(inspectionId: string): Promise<ReportSectionComposition> {
    const r = await apiClient.get(`${base(inspectionId)}/sections`);
    return r.data as ReportSectionComposition;
  },

  /**
   * Replaces the whole composition. The COMPLETE list is sent every time — the
   * server derives the sparse override document, so the UI never builds one.
   */
  async setSectionComposition(
    inspectionId: string,
    version: number,
    sections: ReportSectionCompositionInput[],
  ): Promise<ReportSectionComposition> {
    const r = await apiClient.put(`${base(inspectionId)}/sections`, { version, sections });
    return r.data as ReportSectionComposition;
  },

  // ── Tenant report settings ─────────────────────────────────────────────────
  /** Full configuration, including allocation policy. Settings screen only. */
  async getSettings(): Promise<ReportSettings> {
    const r = await apiClient.get('/settings/report');
    return r.data as ReportSettings;
  },

  /**
   * Display-only subset for report screens.
   *
   * A projection of the same tenant settings row, reachable with the permission
   * a report author already holds — so the report screen can show the number
   * format without the author gaining the company-profile settings surface.
   */
  async getNumberPresentation(): Promise<ReportNumberPresentation> {
    const r = await apiClient.get('/settings/report/presentation');
    return r.data as ReportNumberPresentation;
  },
  async updateSettings(dto: Partial<ReportSettings>): Promise<ReportSettings> {
    const r = await apiClient.patch('/settings/report', dto);
    return r.data as ReportSettings;
  },
};
